import { Decimal } from 'decimal.js';

import {
  BulkApplyDiscountPayload,
  BulkEditPayload,
  BulkPriceChangePayload,
} from 'apis/restaurant-api/merchant-admin/menu2/types';

import { ValidPriceChangeFormValues } from './validation';

type PriceChangeType = Extract<
  ValidPriceChangeFormValues['type'],
  'singlePrice' | 'changeByAmount' | 'changeByPercentage'
>;
type AppliedDiscountType = Extract<
  ValidPriceChangeFormValues['type'],
  'changeByDiscountAmount' | 'changeByDiscountPercentage'
>;

const getPriceChangeType = (type: ValidPriceChangeFormValues['type']): BulkPriceChangePayload['change_type'] => {
  switch (type) {
    case 'singlePrice':
      return 'absolute';
    case 'changeByAmount':
      return 'relative';
    case 'changeByPercentage':
      return 'relative-percentage';
    default:
      throw new Error(`No matching case for ${type}`);
  }
};

const getPriceChangeAmount = (validFormValues: ValidPriceChangeFormValues): BulkPriceChangePayload['change_amount'] => {
  switch (validFormValues.type) {
    case 'changeByAmount':
      return new Decimal(Number(validFormValues.changeAmount))
        .times(validFormValues.operation === 'addition' ? 1 : -1)
        .toString();
    case 'changeByPercentage':
      return new Decimal(Number(validFormValues.changePercentage))
        .times(validFormValues.operation === 'addition' ? 1 : -1)
        .toString();
    default:
      return new Decimal(Number(validFormValues.singlePrice)).toString();
  }
};

const getAppliedDiscountType = (
  type: ValidPriceChangeFormValues['type'],
): BulkApplyDiscountPayload['discount_type'] => {
  switch (type) {
    case 'changeByDiscountAmount':
      return 'absolute';
    case 'changeByDiscountPercentage':
      return 'relative';
    default:
      // type check for this case happens in buildBulkActionUpdatePricePayload
      // but TS is not able to narrow it on its own
      throw new Error(`No matching case for ${type}`);
  }
};

const getAppliedDiscountAmount = (
  validFormValues: ValidPriceChangeFormValues,
): BulkApplyDiscountPayload['discount_amount'] => {
  switch (validFormValues.type) {
    case 'changeByDiscountAmount':
      return new Decimal(Number(validFormValues.applyDiscountAmount)).absoluteValue().toString();
    case 'changeByDiscountPercentage':
      return new Decimal(Number(validFormValues.applyDiscountPercentage)).absoluteValue().toString();
    default:
      // type check for this case happens in buildBulkActionUpdatePricePayload
      // but TS is not able to narrow it on its own
      throw new Error(`No matching case for ${validFormValues.type}`);
  }
};

const isPriceChangeType = (type: ValidPriceChangeFormValues['type']): type is PriceChangeType =>
  ['singlePrice', 'changeByAmount', 'changeByPercentage'].includes(type);

const isDiscountChangeType = (type: ValidPriceChangeFormValues['type']): type is AppliedDiscountType =>
  ['changeByDiscountAmount', 'changeByDiscountPercentage'].includes(type);

export const buildBulkActionUpdatePricePayload = (
  validFormValues: ValidPriceChangeFormValues,
): Partial<BulkEditPayload> => ({
  ...(isPriceChangeType(validFormValues.type) ? buildBulkActionPriceChangePayload(validFormValues) : {}),
  ...(isDiscountChangeType(validFormValues.type) ? buildBulkActionAppliedDiscountPayload(validFormValues) : {}),
});

export const buildBulkActionPriceChangePayload = (
  validFormValues: ValidPriceChangeFormValues,
): Partial<BulkEditPayload> => {
  return {
    price: {
      change_type: getPriceChangeType(validFormValues.type),
      change_amount: getPriceChangeAmount(validFormValues),
    },
  };
};

export const buildBulkActionAppliedDiscountPayload = (
  validFormValues: ValidPriceChangeFormValues,
): Partial<BulkEditPayload> => {
  return {
    apply_discount: {
      discount_type: getAppliedDiscountType(validFormValues.type),
      discount_amount: getAppliedDiscountAmount(validFormValues),
    },
  };
};
